import { reportError } from './errors/reportError';

export type SuggestionsQuery = {
  siteId: string;
  searchTerm: string;
  searchFields?: string[];
  returnField?: string;
};

export type Suggestion = {
  title: string;
  url?: string;
};

let suggestionsFilter: string;

let unreadableReported = false;

/**
 * The suggestions body, or null when it is not JSON.
 *
 * The gateway answers everything with a 200, its own errors included, so the status says
 * nothing about what came back. A body that would not parse reached a real iPhone anyway
 * (Airbrake group 4424552647460187255), and every query shape this file can build comes back
 * as JSON from the gateway, which keeps no logs. What arrived is the only evidence there will
 * be, so it is reported with enough of the body to say what it was — once per page, since a
 * connection that does this does it on every keystroke. The dropdown shows nothing instead
 * of throwing.
 */
async function readJson(res: Response): Promise<any> {
  const body = await res.text();

  try {
    return JSON.parse(body);
  } catch (parseError) {
    if (!unreadableReported) {
      unreadableReported = true;
      reportError(new Error('Search suggestions: the response was not JSON'), {
        source: 'searchSuggestions',
        parseError: String(parseError),
        status: res.status,
        contentType: res.headers.get('content-type'),
        length: body.length,
        sample: body.slice(0, 100),
      });
    }

    return null;
  }
}

class Search {
  baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async suggestions(query: SuggestionsQuery): Promise<Suggestion[]> {
    const buildFilters = () => {
      const filters = {
        site_id: query.siteId,
        status: 'active',
        customer_searchable: 1,
        buyable: 1,
      };

      let queries: string[] = [];

      Object.keys(filters).forEach((key) => {
        const value = (filters as any)[key];
        queries.push(`(term field=${key} '${value}')`);
      });

      return `(and ${queries.join(' ')})`;
    };

    if (!suggestionsFilter) {
      suggestionsFilter = encodeURIComponent(buildFilters());
    }

    const buildQuery = (searchTerm: string): string => {
      searchTerm = searchTerm.trim().toLowerCase();
      if (!searchTerm) {
        return '';
      }

      searchTerm = searchTerm.replace(/ma?g?a?z?i?n?e? *$/i, '');
      searchTerm = searchTerm.replace(/  +/g, ' ');

      if (searchTerm.length > 1 && /[+&|!(){}\[\]^"~*?:\\/-]/.test(searchTerm.slice(-1))) {
        searchTerm = searchTerm.slice(0, -1);
      }

      searchTerm = searchTerm.replace(/[+&|!(){}\[\]^"~*?:\\/-]/g, '\\$&');
      searchTerm += '*';

      return searchTerm;
    };

    const options = encodeURIComponent(
      JSON.stringify({ fields: query.searchFields || ['title', 'authors', 'product_identifiers'] })
    );

    const field = query.returnField || 'title';

    const endpoint =
      `${this.baseUrl}/search?q=` +
      encodeURIComponent(buildQuery(query.searchTerm)) +
      `&q.options=${options}` +
      `&sort=total_sold+desc,_score+desc,review_count+desc,created_at+desc,description+asc&return=${encodeURIComponent(
        `${field},link`
      )}&fq=` +
      suggestionsFilter;

    return await fetch(endpoint).then(async (res) => {
      if (res.status === 200) {
        const json = await readJson(res);

        if (json?.hits?.found > 0) {
          return json.hits.hit
            .map((hit: any) => {
              const title = hit?.fields?.[field];

              if (title instanceof Array) {
                return title.map((t: string, i: number) => {
                  return { title: t };
                });
              }

              return { title: hit?.fields?.[field], url: hit?.fields?.link };
            })
            .flat();
        }
      }

      return [];
    });
  }
}

// Mirrors the shape useSearchAutoComplete expected from jshop-sdk's client, so the
// hook ports unchanged. The base URL is injected at build time by vite.config.ts,
// exactly as webpack's DefinePlugin does it for the legacy bundle.
const Api = {
  search: new Search(process.env.NEXT_PUBLIC_SEARCH_BASE_URL as string),
};

export default Api;
